import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';

import { api } from '@connected/api-client';
import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { generateRedirectUrl } from '@connected/api-client/lib/portal';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Button } from '@connected/ui/components/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@connected/ui/components/dropdown-menu';
import { Skeleton } from '@connected/ui/components/skeleton';
import { Spinner } from '@connected/ui/components/spinner';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ArrowUpRight, ChevronDown, ChevronRight, Copy, EllipsisVertical, File, PlusIcon, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { DataTable } from '@/components/data-table';
import { ExpandedRowsMobile } from '@/components/expanded-row';
import { LoadingPage } from '@/components/loading-page';
import SearchInput from '@/components/search-input';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

export type ProjectData = components['schemas']['ListElectricalProjectQueryResultProject'];
type ProjectCreateQuery = components['schemas']['ChangeElectricalProjectStateCommand'];

export const Route = createFileRoute('/_auth/dashboard/organizations/$organizationId/requests/')({
    component: RouteComponent,
    validateSearch: z.object({
        search: z.string().optional(),
    }),
});

function CreateProject({
    type,
}: {
    type: 'Planned' | 'Request';
}) {
    const { t } = useTranslation('dashboard');
    const [isDisabled, setDisabled] = useState<boolean>(false);
    const { organizationId } = Route.useParams();
    const createMutation = api.useMutation('post', '/api/v2/electrical/projects/create', {
        onMutate: () => {
            toast.info(t('dashboard.projects.message.inMutation'));
            setDisabled(true);
        },
        onError: (error) => {
            logger.error(
                {
                    system: 'CreateElectricalProjectCommandResult',
                    error,
                },
                { apiHttpMethod: 'Post', apiUrl: '/api/v2/electrical/projects/create' },
            );
            toast.error(error?.Message ?? t('dashboard.general.messages.error'));
        },
    });

    const setStatus = api.useMutation('put', '/api/v2/electrical/projects/status', {
        onError: (error) => {
            logger.error(
                {
                    system: 'EditElectricalInspectionNormItemCommand',
                    error,
                },
                { apiHttpMethod: 'Put', apiUrl: '/api/v2/electrical/inspectionnorms/items/edit' },
            );
            toast.error(error?.Message ?? t('dashboard.general.messages.error'));
        },
        onSettled: () => {
            queryClient.invalidateQueries(
                elektraAppQueryOptions.organisation.projects.projectListOptions({ organizationId }),
            );
            setDisabled(false);
        },
    });

    return (
        <Button
            disabled={isDisabled}
            variant="link"
            className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background"
            onClick={() => {
                createMutation.mutateAsync(
                    {
                        body: {
                            OrganizationId: organizationId,
                        },
                    },
                ).then((response) => {
                    if (response.ElectricalProjectVersionId) {
                        const url = generateRedirectUrl(
                            {
                                redirectHost: import.meta.env.VITE_DOMAIN_ELEKTRA_APP,
                                redirectPath: `/projects/${response.ElectricalProjectVersionId}/applications/request-concerning`,
                            },
                        );
                        window.open(
                            url ?? '',
                            '_blank',
                        );
                        toast.success(
                            <a href={url ?? ''} target="_blank" rel="noopeners noreferrer" className="font-semibold">
                                {t('dashboard.projects.message.projectCreated')}
                                <br />
                                {t('dashboard.projects.create.requests.openProjectLink')}
                            </a>,
                        );
                    }
                    if (response.Result === 'Success') {
                        setStatus.mutateAsync({
                            body: {
                                ElectricalProjectVersionId: response.ElectricalProjectVersionId ?? '',
                                Status: type as ProjectCreateQuery['Status'],
                            },
                        });
                    }
                });
            }}
        >
            {isDisabled ? <Spinner /> : <PlusIcon className="w-5 h-5" />}
            {t('dashboard.projects.create.requests.title')}
        </Button>
    );
}

function RouteComponent() {
    const { t } = useTranslation('dashboard');
    const params = Route.useParams();
    const navigate = Route.useNavigate();
    const { currentUser } = useAuthentication();

    const [expandedId, setExpandedId] = useState<string | null>(null);

    const search = Route.useSearch();

    const hasCorrectRole = currentUser?.Roles?.some(
        role => role?.Name === 'Applicatie beheerder' || role?.Name === 'Elektra manager',
    );

    const projectsQuery = useQuery(
        elektraAppQueryOptions.organisation.projects.requestListOptions({
            organizationId: params.organizationId,
        }),
    );

    const copyProjectQuery = api.useMutation('post', '/api/v2/electrical/projects/copy', {
        onSettled: () => {
            queryClient.invalidateQueries(
                elektraAppQueryOptions.organisation.projects.projectListOptions({ organizationId: params.organizationId }),
            );
        },
    });

    const columns = useMemo<ColumnDef<ProjectData>[]>(() => ([
        {
            accessorKey: 'ProjectNumber',
            meta: {
                sticky: true,
                width: 10,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.projects.table.headers.projectNumber')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell
                    variant="bold"
                >
                    <Button
                        variant="ghost"
                        className="sm:hidden mr-2"
                        onClick={() => setExpandedId(prev => prev === row?.original?.CurrentElectricalProjectVersionId ? null : row?.original?.CurrentElectricalProjectVersionId ?? null)}
                    >
                        {expandedId === row?.original?.CurrentElectricalProjectVersionId ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </Button>
                    {row?.original?.ProjectNumber || '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Object',
            meta: {
                sticky: false,
                width: 200,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.projects.table.headers.object-location')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell
                    variant="bold"
                >
                    <div className="flex flex-col gap-1">
                        {row?.original?.Object?.Street}
                        {' '}
                        {row?.original?.Object?.StreetNumber}
                        <span className="font-normal">
                            {row?.original?.Object?.PostalCode}
                            {' '}
                            {row?.original?.Object?.Locality}
                        </span>
                    </div>
                    {!row?.original?.Object?.Street && !row?.original?.Object?.StreetNumber && !row?.original?.Object?.PostalCode && !row?.original?.Object?.Locality ? '-' : ''}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'CustomerName',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.projects.table.headers.customer-name')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell
                    variant="default"
                >
                    {row.original?.CustomerName || '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'ReportKinds',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.projects.table.headers.inspection-type')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell
                    variant="default"
                >
                    {row.original?.Reports?.map(report => report.Name)?.join(', ') || '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Status',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.projects.table.headers.status')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                let color = '';
                switch (row.original.Status) {
                    case 'Request':
                        color = '#FAC515';
                        break;
                    case 'PriceQuote':
                        color = '#EF6820';
                        break;
                    case 'Planned':
                        color = '#4E5BA6';
                        break;
                    case 'Failed':
                        color = '#F04438';
                        break;
                    case 'Complete':
                        color = '#17B26A';
                        break;
                    case 'RepeatInspection':
                        color = '#2E90FA';
                        break;
                }

                return row.original.Status && color
                    ? (
                            <DataTable.Cell
                                variant="colorLabel"
                                color={color}
                            >
                                {t(`dashboard.projects.table.columns.status.${row.original.Status.toLowerCase()}`)}
                            </DataTable.Cell>
                        )
                    : (
                            <DataTable.Cell
                                variant="default"
                            >
                                -
                            </DataTable.Cell>
                        );
            },
        },
        {
            accessorKey: 'Users',
            meta: {
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.projects.table.headers.inspectors')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const filtered = row.original?.Users?.filter(x => x.Kind === 'Inspector' || x.Kind === 'CertifiedInspector') || [];
                const uniqueNames = [...new Set(filtered.map(u => u.DisplayName))];
                return (
                    <DataTable.Cell
                        variant="default"
                    >
                        {uniqueNames.length ? uniqueNames.join(' ') : '-'}
                    </DataTable.Cell>
                );
            },
        },
        {
            accessorKey: 'LastActivityDate',
            meta: {
                sticky: false,
                width: 200,
                mobileHidden: true,
            },
            header: ({ column }) => (
                <DataTable.Header
                    column={column}
                    variant="sortReverse"
                >
                    {t('dashboard.projects.table.headers.updatedAt')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell
                    variant="default"
                >
                    {(() => {
                        const dateValue = row.original.LastActivityDate;
                        if (!dateValue)
                            return '-';
                        const date = new Date(dateValue);
                        if (Number.isNaN(date.getTime()))
                            return '-';
                        return date.toLocaleDateString('nl-NL', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                        });
                    })()}
                </DataTable.Cell>
            ),
        },
        {
            id: 'actions',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: ({ row }) => (
                <DropdownMenu>
                    <DropdownMenuTrigger className="flex items-center">
                        <EllipsisVertical className="text-text-primary/40 size-6" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="flex flex-col gap-1">
                        <DropdownMenuItem className="px-2">
                            <ArrowUpRight className="text-text-primary/40 size-5" />
                            <a
                                href={`${generateRedirectUrl({
                                    redirectHost: import.meta.env.VITE_DOMAIN_ELEKTRA_APP,
                                    redirectPath: `/projects/${row?.original?.CurrentElectricalProjectVersionId}/applications/request-concerning`,
                                })}`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button
                                    className="w-fit border-0 px-0"
                                    variant="outline"
                                >
                                    {t('dashboard.projects.electraApp')}
                                </Button>
                            </a>
                        </DropdownMenuItem>
                        {/* <DropdownMenuItem className="px-2">
                            <ArrowUpRight className="text-text-primary/40 size-5" />
                            <a
                                href={buildUrl(
                                    import.meta.env.VITE_DOMAIN_PORTAAL,
                                    `/customer/locations/${row?.original?.Object?.ObjectId}/electrical/projects/${row?.original?.ProjectId}`,
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Button
                                    className="w-fit border-0 px-0"
                                    variant="outline"
                                    disabled={row?.original?.Object?.ObjectId == null}
                                >
                                    {t('dashboard.projects.portalApp')}
                                </Button>
                            </a>
                        </DropdownMenuItem> */}
                        <DropdownMenuItem className="px-2">
                            <File className="text-text-primary/40 size-5" />
                            <Button
                                className="w-fit border-0 px-0"
                                variant="outline"
                                onClick={() => {
                                    navigate({
                                        to: `/dashboard/organizations/$organizationId/projects/$projectVersionId/documents`,
                                        params: { projectVersionId: row.original.CurrentElectricalProjectVersionId! },
                                        search: { tab: 'rapports' },
                                    });
                                }}
                            >
                                {t('dashboard.projects.documents.title')}
                            </Button>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="px-2">
                            <Copy className="text-text-primary/40 size-5" />
                            <Button
                                onClick={async () => {
                                    if (await showConfirm('Weet je zeker dat je dit project wilt kopiëren? ')) {
                                        toast.info(t('dashboard.projects.message.projectCopying'));
                                        await copyProjectQuery.mutateAsync({
                                            body: {
                                                ElectricalProjectId: row?.original?.ElectricalProjectId ?? '',
                                            },
                                        }).then((response) => {
                                            if (response.ElectricalProjectVersionId) {
                                                const url = generateRedirectUrl({
                                                    redirectHost: import.meta.env.VITE_DOMAIN_ELEKTRA_APP,
                                                    redirectPath: `/projects/${response.ElectricalProjectVersionId}/applications/request-concerning`,
                                                },
                                                );
                                                toast.success(
                                                    <a href={url ?? '_'} target="_blank" rel="noopeners noreferrer" className="font-semibold">
                                                        {t('dashboard.projects.message.projectCreated')}
                                                        <br />
                                                        {t('dashboard.projects.create.requests.openProjectLink')}
                                                    </a>,
                                                    {
                                                        duration: 10000,
                                                    },
                                                );
                                            }
                                        });
                                    }
                                }}
                                className="w-fit border-0 px-0"
                                variant="outline"
                            >
                                {t('dashboard.projects.projectsCopy')}
                            </Button>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="px-2">
                            <Trash2 className="text-text-primary/40 size-5" />
                            <Button
                                disabled
                                className="w-fit border-0 px-0"
                                variant="outline"
                            >
                                {t('dashboard.projects.deleteProject')}
                            </Button>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
        },
    ]), [copyProjectQuery, expandedId, navigate, t]);

    const tableColumns = useMemo(
        () => projectsQuery.isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [columns, projectsQuery.isLoading],
    );

    const tableData = useMemo<ProjectData[]>(
        () => {
            if (projectsQuery.isError) {
                toast.error(projectsQuery.error?.Message);
                return [];
            }

            const searchTerm = search.search?.toLowerCase() || '';
            return projectsQuery.data?.Projects?.filter((proj) => {
                if (!searchTerm)
                    return true;

                return [
                    proj.ProjectId || '',
                    proj.ElectricalProjectId || '',
                    proj.CurrentElectricalProjectVersionId || '',
                    proj.Object?.Street || '',
                    proj.Object?.StreetNumber || '',
                    proj.Object?.PostalCode || '',
                    proj.Object?.Locality || '',
                    proj.ProjectNumber || '',
                    proj.LastActivityDate || '',
                    proj.CustomerName || '',
                    proj.Reports?.map(report => report.Name)?.join(', ') || '-',
                    proj.Status ? t(`dashboard.projects.table.columns.status.${proj.Status.toLowerCase()}`) : '',
                    proj.Users?.length ? proj.Users?.map(user => user.DisplayName) : '-',
                    proj.CreateDate
                        ? new Date(proj.CreateDate).toLocaleDateString('nl-NL', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                            })
                        : '',
                ].some(field => field?.toString().toLowerCase().includes(searchTerm));
            }) || [];
        },
        [projectsQuery.isError, projectsQuery.data?.Projects, projectsQuery.error?.Message, search.search, t],
    );

    if (projectsQuery.isLoading) {
        return <LoadingPage />;
    }

    return (
        <div>
            <div className="flex flex-col gap-8 justify-start">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                    <div className="flex flex-col gap-1">
                        <h3 className="text-2xl font-semibold">
                            {t('dashboard.requests.title')}
                        </h3>
                        <p className="text-md text-text-primary/70">
                            {t('dashboard.requests.subtitle')}
                        </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-5">
                        {(hasCorrectRole || currentUser?.IsAdmin) && <CreateProject type="Planned" /> }
                        <SearchInput route={Route} />
                    </div>
                </div>
                <DataTable
                    columns={tableColumns}
                    data={tableData}
                    pageSize={10}
                    expandedRow={row =>
                        expandedId && expandedId === row?.original?.CurrentElectricalProjectVersionId
                            ? (
                                    <ExpandedRowsMobile columns={columns} data={row.original} />
                                )
                            : null}
                />
            </div>
        </div>
    );
}
